import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Box } from '@mui/material';
import { Warning } from '@mui/icons-material';
import { megaphoneMumAPI } from '../services/api';

const SITE_LABELS: Record<string, string> = {
  spoutlore: 'Spout Lore',
  mallbrats: 'Mall Brats',
};

interface TokenHealth {
  reachable: boolean;
  ok: boolean;
  error: string | null;
  sites: Record<
    string,
    { alive: boolean; expired?: boolean; detail?: string; status?: number; signin?: boolean }
  >;
}

/**
 * Global banner that surfaces a dead Megaphone CMS session.
 *
 * Polls /megaphone/mum/token-health (a real authenticated GraphQL probe via
 * the cms-browser-proxy) every 60s. Renders NOTHING unless the proxy is
 * reachable AND at least one show's session is dead — so in the normal case
 * it's invisible. When a session dies (someone logged that Megaphone account
 * out / in, or it hit max-lifetime), MUM stops collecting downloads, and this
 * is the prompt to paste a fresh session_id.
 *
 * Degrades silently: if the user isn't admin-authed, the proxy is down, or
 * CMS_PROXY_URL isn't configured (local dev), it shows nothing.
 */
const MegaphoneTokenBanner: React.FC<{ onFix: (slug: string) => void }> = ({ onFix }) => {
  const { data } = useQuery<TokenHealth>({
    queryKey: ['mum-token-health'],
    queryFn: megaphoneMumAPI.tokenHealth,
    refetchInterval: 60_000,
    retry: false,                 // a 401 (not admin) or proxy-down → just stay quiet
    refetchOnWindowFocus: true,   // re-check when they come back to the tab
  });

  if (!data || !data.reachable || !data.sites) return null;

  // Only fire on a GENUINE auth failure (`expired`), never a transient probe
  // hiccup — a timeout / 302 / network blip leaves `alive=false` but
  // `expired=false`, and must not nag the user to re-paste a working session.
  const deadEntries = Object.entries(data.sites).filter(([, s]) => s.expired);
  const deadSlugs = deadEntries.map(([slug]) => slug);
  const dead = deadSlugs.map((slug) => SITE_LABELS[slug] || slug);

  if (dead.length === 0) return null;

  const names =
    dead.length === 1
      ? dead[0]
      : dead.slice(0, -1).join(', ') + ' and ' + dead[dead.length - 1];

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
      <Alert
        severity="warning"
        icon={<Warning fontSize="inherit" />}
        sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 0 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => onFix(deadSlugs[0])}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Fix {dead.length === 1 ? dead[0] : 'now'} →
          </Button>
        }
      >
        <strong>{names}</strong> Megaphone {dead.length === 1 ? 'session has' : 'sessions have'} expired —
        downloads data has stopped collecting. Paste a fresh session ID under{' '}
        <em>Megaphone → MUM Setup</em>.
      </Alert>
    </Box>
  );
};

export default MegaphoneTokenBanner;
